import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { packingNoteBusiness } from "api/bussiness/package-note/packing-note-business";
import { PackingNotesPermission } from "commons/enum/permission";
import { dateTimeDisplay } from "commons/ultilities/date/dayjs";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IPackingNoteItemProperties } from "mobx-stores/_models/packing-note/packing-note-item";
import { IProduct } from "mobx-stores/_models/products/product";
import { useStores } from "mobx-stores/_root-store";
import { CustomerDealingAndActiveInfo } from "pages/common/customer-dealing-and-active-info";
import { ProductNameAndActiveInfo } from "pages/common/product-name-and-active-info";
import { MouseEventHandler, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NumberText } from "share-components/atomics/number-text";
import { PropertyInfo } from "share-components/atomics/property-info";
import {
  EditButton,
  GoBackIconButton,
  PrintPreviewButton,
  RemoveButton,
} from "share-components/mui-wrap/button/action-button";
import { PageContentScrollableContainer } from "share-components/templates/page/page-content-container";
import { PageDetailContainer } from "share-components/templates/page/page-detail-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";
import { EditPackingNote } from "../edit-packing-note/edit-packing-note";
import { PackingNotePrintPreview } from "./packing-print-preview";
export const PackingNoteDetail = observer(() => {
  const { packingNoteId } = useParams();
  const {
    packingNoteManagement: {
      getPackingNoteDetail,
      openEdit,
      packingPrintingManagement: { open, selectedPackingNoteItem },
    },
  } = useStores();
  useEffect(() => {
    if (packingNoteId) {
      getPackingNoteDetail(packingNoteId);
    }
  }, [getPackingNoteDetail, packingNoteId]);
  return (
    <PageDetailContainer>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <PageContentScrollableContainer>
        <Content></Content>
      </PageContentScrollableContainer>

      <>{openEdit && <EditPackingNote></EditPackingNote>}</>
      <>{open && selectedPackingNoteItem && <PackingNotePrintPreview />}</>
    </PageDetailContainer>
  );
});
type HeaderProps = {};
const Header = observer((props: HeaderProps) => {
  const {
    packingNoteManagement: {
      packingNoteDetail,
      setProp: setPackingNoteManagementProp,
    },
    deleteConfirmationStore: { setProp, setDeletionCallback },
  } = useStores();
  const navigate = useNavigate();
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const onDelete = useCallback(
    async (id: string) => {
      if (id) {
        await packingNoteBusiness.delete(id);
        goBack();
      }
    },
    [goBack],
  );
  const removeHandler = useCallback(() => {
    setProp("open", true);
    setDeletionCallback(() => {
      if (!packingNoteDetail?.id) return;
      onDelete(packingNoteDetail?.id);
    });
  }, [packingNoteDetail, onDelete, setDeletionCallback, setProp]);

  const editHandler = useCallback(() => {
    setPackingNoteManagementProp("openEdit", true);
  }, [setPackingNoteManagementProp]);

  return (
    <>
      <Stack
        direction={"row"}
        justifyItems={"center"}
        spacing={1}
        alignItems={"center"}
      >
        <GoBackIconButton onClick={goBack}></GoBackIconButton>
        <Typography variant="subtitle2">{`Thông tin chi tiết `}</Typography>
      </Stack>
      <Stack direction={"row"} justifyItems={"center"} spacing={1}>
        <RemoveButton
          accessPermission={[PackingNotesPermission.Delete]}
          onClick={removeHandler}
        ></RemoveButton>
        <EditButton
          accessPermission={[PackingNotesPermission.Update]}
          onClick={editHandler}
        ></EditButton>
      </Stack>
    </>
  );
});

const Content = observer(() => {
  const {
    packingNoteManagement: { packingNoteDetail },
  } = useStores();
  return (
    <Stack>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <PropertyInfo
            title="Ngày:"
            value={dateTimeDisplay(packingNoteDetail?.date || "")}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Số phiếu:"
            value={packingNoteDetail?.number || ""}
          ></PropertyInfo>
        </Grid>
        <Grid item xs={6}>
          <PropertyInfo
            title="Đơn đặt hàng:"
            value={packingNoteDetail?.purchaseOrder?.poNumber || ""}
          ></PropertyInfo>
        </Grid>
        <Grid item xs={6}>
          <PropertyInfo
            title="Tên giao dịch:"
            value={
              packingNoteDetail?.customer ? (
                <CustomerDealingAndActiveInfo
                  customer={packingNoteDetail?.customer}
                ></CustomerDealingAndActiveInfo>
              ) : (
                ""
              )
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Lệnh sản xuất:"
            value={
              (packingNoteDetail?.workOrderPO?.woNumber ??
                packingNoteDetail?.workOrder?.woNumber) ||
              ""
            }
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="Người đóng gói:"
            value={packingNoteDetail?.kcsPackage || ""}
          ></PropertyInfo>
        </Grid>

        <Grid item xs={6}>
          <PropertyInfo
            title="KCS Kiểm:"
            value={packingNoteDetail?.kcsTest || ""}
          ></PropertyInfo>
        </Grid>
      </Grid>
      <ListPackingNoteItem></ListPackingNoteItem>
    </Stack>
  );
});

const ListPackingNoteItem = observer(() => {
  const {
    packingNoteManagement: { packingNoteDetail },
  } = useStores();
  return (
    <Box
      flex={1}
      sx={{
        borderRadius: (theme) => theme.shape.borderRadius,
        padding: "10px",
        backgroundColor: (theme) => theme.palette.background.default,
        marginTop: "20px",
      }}
    >
      {packingNoteDetail?.items.map((x) => (
        <PackingNoteItem key={x.id} packingNoteItem={x}></PackingNoteItem>
      ))}
    </Box>
  );
});
type PackingNoteItemProps = {
  packingNoteItem: IPackingNoteItemProperties;
};
const PackingNoteItem = observer(
  ({ packingNoteItem }: PackingNoteItemProps) => {
    const {
      packingNoteManagement: {
        packingPrintingManagement: { setProp },
      },
    } = useStores();
    const openPrintPreviewHandler: MouseEventHandler<HTMLButtonElement> =
      useCallback(
        (e) => {
          setProp("open", true);
          setProp("selectedPackingNoteItem", cloneDeep(packingNoteItem));
          e.stopPropagation();
        },
        [packingNoteItem, setProp],
      );
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Grid container>
            <Grid item xs={3}>
              <PropertyInfo
                title="Mã hàng:"
                value={packingNoteItem?.product?.code || ""}
              ></PropertyInfo>
            </Grid>
            <Grid item xs={3}>
              <PropertyInfo
                title="Tên hàng:"
                value={
                  packingNoteItem?.product ? (
                    <ProductNameAndActiveInfo
                      product={packingNoteItem?.product as IProduct}
                    ></ProductNameAndActiveInfo>
                  ) : (
                    ""
                  )
                }
              ></PropertyInfo>
            </Grid>
            <Grid item xs={3}>
              <PropertyInfo
                title="Mã vạch:"
                value={packingNoteItem?.product?.barcode || ""}
              ></PropertyInfo>
            </Grid>

            <Grid item xs={3}>
              <PrintPreviewButton
                accessPermission={[PackingNotesPermission.PrintPackingNote]}
                onClick={openPrintPreviewHandler}
              >
                In phiếu đóng gói
              </PrintPreviewButton>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <PropertyInfo
                title="ĐVT:"
                value={packingNoteItem?.product?.unitOfCalculation || ""}
              ></PropertyInfo>
            </Grid>
            <Grid item xs={3}>
              <PropertyInfo
                title="Số lượng (pcs):"
                value={
                  <NumberText value={packingNoteItem?.amount}></NumberText>
                }
              ></PropertyInfo>
            </Grid>

            <Grid item xs={3}>
              <PropertyInfo
                title="TL Tịnh (kg):"
                value={
                  <NumberText value={packingNoteItem?.netWeight}></NumberText>
                }
              ></PropertyInfo>
            </Grid>
            <Grid item xs={3}>
              <PropertyInfo
                title="TL có bì (kg):"
                value={
                  <NumberText value={packingNoteItem?.grossWeight}></NumberText>
                }
              ></PropertyInfo>
            </Grid>

            <Grid item xs={3}>
              <PropertyInfo
                title="Hạn tái kiểm:"
                value={dateTimeDisplay(packingNoteItem.expiredDate)}
              ></PropertyInfo>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  },
);
